import { useState } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { WhatsAppIcon } from './icons/WhatsAppIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WHATSAPP_URL = 'https://wa.me/491787261209';

// Kontaktformular (Startseite + Über uns). testIdPrefix: '' oder 'about-'
export const ContactForm = ({ testIdPrefix = '', idSuffix = '' }) => {
  const [formData, setFormData] = useState({ name: '', company: '', email: '', message: '', consent: false });
  const [formStatus, setFormStatus] = useState({ loading: false, success: false, error: '' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent) {
      setFormStatus({ loading: false, success: false, error: 'Bitte stimme der Datenschutzerklärung zu.' });
      return;
    }
    setFormStatus({ loading: true, success: false, error: '' });
    try {
      await axios.post(`${API}/contact`, formData);
      setFormStatus({ loading: false, success: true, error: '' });
      setFormData({ name: '', company: '', email: '', message: '', consent: false });
    } catch (err) {
      setFormStatus({ loading: false, success: false, error: 'Es gab einen Fehler. Bitte versuche es später erneut.' });
    }
  };

  const fields = [
    { id: 'name', label: 'Name *', type: 'text', placeholder: 'Dein Name', autoComplete: 'name' },
    { id: 'company', label: 'Unternehmen / Betrieb *', type: 'text', placeholder: 'Name deines Betriebs', autoComplete: 'organization' },
    { id: 'email', label: 'E-Mail Adresse *', type: 'email', placeholder: 'deine@email.de', autoComplete: 'email' },
  ];

  return (
    <form onSubmit={handleSubmit} className="card-ink p-6 md:p-10 space-y-6" data-testid={`${testIdPrefix}contact-form`}>
      {fields.map((f) => (
        <div key={f.id}>
          <Label htmlFor={`${f.id}${idSuffix}`} className="field-label">{f.label}</Label>
          <Input
            id={`${f.id}${idSuffix}`}
            name={f.id}
            type={f.type}
            required
            autoComplete={f.autoComplete}
            value={formData[f.id]}
            onChange={handleInputChange}
            placeholder={f.placeholder}
            data-testid={`${testIdPrefix}contact-${f.id}`}
          />
        </div>
      ))}
      <div>
        <Label htmlFor={`message${idSuffix}`} className="field-label">Anfrage *</Label>
        <Textarea
          id={`message${idSuffix}`}
          name="message"
          required
          value={formData.message}
          onChange={handleInputChange}
          placeholder="Beschreibe deine Anfrage..."
          data-testid={`${testIdPrefix}contact-message`}
        />
      </div>
      <div className="flex items-start gap-3">
        <Checkbox
          id={`consent${idSuffix}`}
          checked={formData.consent}
          onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, consent: checked }))}
          className="mt-1"
          data-testid={`${testIdPrefix}contact-consent`}
        />
        <Label htmlFor={`consent${idSuffix}`} className="text-sm leading-relaxed cursor-pointer font-normal text-ink/85">
          Ich bin einverstanden, dass gastro-beratung.de meine Angaben speichert und mich zur Beantwortung meiner Anfrage kontaktieren darf.
        </Label>
      </div>

      {formStatus.error && (
        <p className="text-red-700 font-medium" data-testid={`${testIdPrefix}form-error`}>{formStatus.error}</p>
      )}
      {formStatus.success && (
        <p className="text-purple font-medium" data-testid={`${testIdPrefix}form-success`}>
          Vielen Dank! Deine Anfrage wurde gesendet. Wir melden uns in Kürze bei dir.
        </p>
      )}

      <button
        type="submit"
        disabled={formStatus.loading}
        className="btn btn--purple w-full sm:w-auto"
        data-testid={`${testIdPrefix}contact-submit`}
      >
        {formStatus.loading ? 'Wird gesendet...' : 'Anfrage senden'}
      </button>
    </form>
  );
};

// Text-Spalte neben dem Formular inkl. WhatsApp-Button
export const ContactAside = ({ title, intro, testIdPrefix = '' }) => (
  <div>
    <p className="text-lg mb-8 max-w-[52ch]">{intro}</p>
    <p className="font-display font-bold text-2xl md:text-[1.7rem] leading-tight mb-6 max-w-[20ch]">{title}</p>
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="btn btn--wa"
      data-testid={`${testIdPrefix}whatsapp-cta`}
    >
      <WhatsAppIcon />
      WhatsApp
    </a>
  </div>
);

export default ContactForm;
